import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { BACKEND } from "../../config/configEnvs";
import { toast } from "react-toastify";
import {
  FaBarcode,
  FaCommentDollar,
  FaEdit,
  FaExternalLinkAlt,
  FaMicrophoneAlt,
  FaPlay,
  FaPlus,
  FaPlusCircle,
  FaStop,
  FaTrash,
  FaTrashAlt,
} from "react-icons/fa";
import { Button } from "@mui/material";

const EditarPago = () => {
  const navigate = useNavigate();
  const { registerId } = useParams();

  // Primer Tab

  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("");

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [datePayment, setDatePayment] = useState("");
  const [amount, setAmount] = useState(0);
  const [advancePayment, setAdvancePayment] = useState(0);
  const [status, setStatus] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [isPayAdvance, setIsPayAdvance] = useState(false);

  const fetchRegister = async () => {
    try {
      const response = await axios.get(
        `${BACKEND}/api/pagosTrabajadores/${registerId}`
      );
      setSelectedWorker(response.data.worker.id);
      setMonth(response.data.month);
      setYear(response.data.year);
      setDatePayment(response.data.datePayment.split("T")[0]);
      setAmount(response.data.amount);
      setAdvancePayment(response.data.advancePayment);
      setStatus(response.data.status);
      setAdditionalNotes(response.data.additionalNotes);
      setExistingImages(response.data.images || []);
      setIsPayAdvance(response.data.isPayAdvance || false);
    } catch (error) {
      console.log(error);
      toast.error("Error obteniendo el registro");
    }
  };

  useEffect(() => {
    fetchRegister();
  }, []);

  // Handlers media

  const handleMediaUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${BACKEND}/api/imagenesPagosTrabajadores`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.doc && response.data.doc.id) {
        return response.data.doc.id;
      } else {
        throw new Error("El ID del archivo no se encontró en la respuesta.");
      }
    } catch (error) {
      toast.error("Error al subir el archivo", {
        style: { zIndex: 9999 },
        position: "top-right",
      });
      return null;
    }
  };

  const handleDeletePrueba = (indexToDelete) => {
    // Eliminar imagenes que aún no se subieron
    setNewImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToDelete)
    );
    toast.success("Imagen eliminada exitosamente");
  };

  const handleDeleteExistingImage = async (id) => {
    if (!id) return;

    const updatedImages = existingImages.filter((image) => image.id !== id);
    const updatedImagesIds = updatedImages.map((image) => image.id);

    try {
      await axios.delete(`${BACKEND}/api/imagenesPagosTrabajadores/${id}`);
      await axios.put(`${BACKEND}/api/pagosTrabajadores/${registerId}`, {
        images: updatedImagesIds,
      });

      setExistingImages(updatedImages);
      toast.success("Imagen eliminada correctamente");
    } catch (error) {
      toast.error("Error al eliminar la imagen: " + error.message);
      console.log(error);
    }
  };

  // Funciones al montar el componente

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(
        `${BACKEND}/api/getUsersIds/getWorkerUsers`
      );
      setWorkers(response.data);
    } catch (error) {
      toast.error("Error obteniendo los trabajadores: " + error);
    }
  };

  useEffect(() => {
    const now = new Date();
    setMonth(now.getMonth() + 1); // Ajustar el mes (de 0-11 a 1-12)
    setYear(now.getFullYear());
    fetchWorkers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let mediaIds = [];

    if (newImages) {
      for (const file of newImages) {
        const id = await handleMediaUpload(file);
        if (id) {
          mediaIds.push(id);
        }
      }
    }

    const existingImagesIds = existingImages.map((image) => image.id);
    const allImagesIds = existingImagesIds.concat(mediaIds);

    try {
      await axios.put(`${BACKEND}/api/pagosTrabajadores/${registerId}`, {
        worker: selectedWorker,
        month,
        year,
        datePayment,
        status,
        advancePayment,
        isPayAdvance,
        amount,
        additionalNotes,
        images: allImagesIds,
      });

      navigate("/home/pagos-trabajadores");
      toast.success("Pago añaido exitosamente");
    } catch (error) {
      toast.error("No se pudo crear el pago: " + error);
    }
  };

  return (
    <div className="flex flex-col h-screen w-full">

      {/* Header */}
      <div className="flex flex-row py-2 bg-gray-100">
        <div className="flex px-4 wflex-row w-full items-center gap-4 justify-between overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4 p-4">
            <FaEdit className="text-2xl text-gray-700" />
            <h2 className="text-2xl font-bold text-gray-700">Editar Pago</h2>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <form className="p-2 flex flex-col gap-2" onSubmit={handleSubmit}>

        <div className="flex flex-row space-x-4 border border-gray-200 shadow-md p-4 text-xs rounded-lg">
          <div className="flex-1">

            <div className="mb-4">
              <label htmlFor="worker" className="block mb-2">
                Trabajador <span className="text-red-500">*</span>
              </label>
              <select
                type="text"
                id="worker"
                value={selectedWorker}
                onChange={(e) => setSelectedWorker(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
                required
              >
                <option value="">Seleccionar trabajador</option>
                {workers.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {worker.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="datePayment" className="block mb-2">
                Fecha de pago: <span className=" text-red-500">*</span>
              </label>
              <input
                type="date"
                id="datePayment"
                name="datePayment"
                value={datePayment}
                onChange={(e) => setDatePayment(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="month" className="block mb-2">
                Mes: <span className=" text-red-500">*</span>
              </label>
              <select
                id="month"
                name="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
                required
              >
                <option value="1">Enero</option>
                <option value="2">Febrero</option>
                <option value="3">Marzo</option>
                <option value="4">Abril</option>
                <option value="5">Mayo</option>
                <option value="6">Junio</option>
                <option value="7">Julio</option>
                <option value="8">Agosto</option>
                <option value="9">Septiembre</option>
                <option value="10">Octubre</option>
                <option value="11">Noviembre</option>
                <option value="12">Diciembre</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="year" className="block mb-2">
                Año: <span className=" text-red-500">*</span>
              </label>
              <select
                type="number"
                id="year"
                name="year"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
                required
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="amount" className="block mb-2">
                Monto
              </label>
              <label className="flex items-center cursor-pointer relative">
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full text-xs border border-gray-300 rounded-lg"
                  required
                />
              </label>
            </div>

            <div className="mb-4">
              <label htmlFor="amount" className="block mb-2">
                Monto adelantado
              </label>
              <label className="flex items-center cursor-pointer relative">
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={advancePayment}
                  onChange={(e) => {
                    setAdvancePayment(e.target.value)
                    if (e.target.value > 0) {
                      setStatus("Adelanto")
                    } else {
                      setStatus("Pendiente")
                    }
                  }}
                  className="w-full text-xs border border-gray-300 rounded-lg"
                  required
                />
              </label>
            </div>

            <div className="mb-4">
              <label htmlFor="year" className="block mb-2">
                Estado:
              </label>
              <select
                type="number"
                id="year"
                name="year"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
                required
              >
                <option value="Pendiente">Pendiente</option>
                <option value="Adelanto">Adelanto</option>
                <option value="Pagado">Pagado</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="additionalNotes" className="block mb-2">
                Comentarios adicionales
              </label>
              <input
                type="text"
                id="additionalNotes"
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-lg"
              />
            </div>

            <div className="mb-4">
              <div className="flex">
                <label htmlFor="images" className="block mb-2">
                  <span className="">Imagenes</span>{" "}
                  <span className="text-gray-500">
                    {" "}
                    - max. 5 archivos de 5mb c/u
                  </span>
                </label>
              </div>

              <input
                type="file"
                id="images"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    const maxFileSize = 5000000; // 5MB
                    const maxFiles = 5;
                    const filesArray = Array.from(e.target.files);

                    const validFiles = filesArray.filter(
                      (file) => file.size <= maxFileSize
                    );

                    if (filesArray.length > maxFiles) {
                      toast.warning(
                        `Puedes seleccionar un máximo de ${maxFiles} archivos.`
                      );
                      return;
                    }

                    if (validFiles.length < filesArray.length) {
                      toast.warning(
                        "Algunos archivos no fueron seleccionados porque superan el tamaño máximo de 5MB."
                      );
                    }

                    setNewImages((prevPruebas) => {
                      const totalFiles = prevPruebas.length + validFiles.length;

                      if (totalFiles > maxFiles) {
                        toast.warning(
                          `Solo puedes tener un máximo de ${maxFiles} archivos.`
                        );
                        return prevPruebas;
                      }

                      return [...prevPruebas, ...validFiles];
                    });
                  }
                }}
                className="w-full border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="w-1/3 p-4">
            <h1 className="mb-2">
              Imagen(es) por subir: {" " + newImages.length}
            </h1>

            <div className="flex flex-col items-center gap-4 max-h-[500px] overflow-y-auto rounded-lg border border-gray-300 p-2 mb-4">
              {newImages && newImages.length > 0 ? (
                newImages.map((image, index) => {
                  const name = image.name || image.filename;
                  const mymeType = image?.type;
                  const fileType = image?.type?.split("/")[0];
                  const url = URL.createObjectURL(
                    image.blob ? image.blob : image
                  );

                  return (
                    <div
                      key={index}
                      className="border p-3 rounded-md shadow-md mb-4 hover:bg-gray-100 transition-300"
                    >
                      <div>
                        {fileType === "image" && (
                          <div className="items-center space-y-6">
                            <div className="flex items-center justify-between">
                              <span className="mb-2">{name}</span>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => window.open(url, "_blank")}
                                  className="p-2 text-blue-500 hover:bg-blue-600 hover:bg-opacity-25 hover:cursor-pointer rounded-md bg-blue-700 bg-opacity-10 flex items-center"
                                >
                                  <FaExternalLinkAlt />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePrueba(index)}
                                  className="p-2 text-red-500 hover:bg-red-600 hover:bg-opacity-25 hover:cursor-pointer rounded-md bg-red-700 bg-opacity-10 flex items-center"
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </div>
                            <img
                              src={url}
                              alt={`Preview ${"index"}`}
                              className="w-full h-auto rounded"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center w-full h-full p-4">
                  No hay archivos subidos.
                </div>
              )}
            </div>

            <h1 className="mb-2">
              Imagen(es) subidas: {" " + existingImages?.length}
            </h1>

            <div className="flex flex-col items-center gap-4 overflow-y-auto rounded-lg border border-gray-300 p-2">
              {existingImages && existingImages?.length > 0 ? (
                existingImages?.map((image, index) => {
                  const name = image.name || image.filename;
                  const fileType = image.mimeType.split("/")[0];
                  const url = image.url;

                  return (
                    <div
                      key={index}
                      className="border p-3 rounded-md shadow-md mb-4 hover:bg-gray-100 transition-300"
                    >
                      <div>
                        {fileType === "image" && (
                          <div className="items-center space-y-6">
                            <div className="flex items-center justify-between">
                              <span className="mb-2">{name}</span>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => window.open(url, "_blank")}
                                  className="p-2 text-blue-500 hover:bg-blue-600 hover:bg-opacity-25 hover:cursor-pointer rounded-md bg-blue-700 bg-opacity-10 flex items-center"
                                >
                                  <FaExternalLinkAlt />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDeleteExistingImage(image.id)
                                  }
                                  className="p-2 text-red-500 hover:bg-red-600 hover:bg-opacity-25 hover:cursor-pointer rounded-md bg-red-700 bg-opacity-10 flex items-center"
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </div>
                            <img
                              src={url}
                              alt={`Preview ${"index"}`}
                              className="w-full h-auto rounded"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center w-full h-full p-4">
                  No hay archivos subidos.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Link to="/home/pagos-trabajadores">
            <Button
              variant="contained"
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                fontFamily: "poppins",
                whiteSpace: "nowrap",
                padding: "4px 24px",
                backgroundColor: "#bdbdbd",
                transform: "all",
                animationDuration: "1000",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#adadad",
                },
              }}
            >
              Cancelar
            </Button>
          </Link>
          {/* <button
            type="submit"
            className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition duration-100 mr-2"
          >
            <FaEdit className="mr-2 text-lg" />
            Actualizar Pago
          </button> */}
          <Button
            variant="contained"
            type="submit"
            endIcon={<FaEdit />}
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              fontFamily: "poppins",
              whiteSpace: "nowrap",
              padding: "4px 24px",
              backgroundColor: "#ff6400",
              transform: "all",
              animationDuration: "1000",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#bb4a03",
              },
            }}
          >
            Actualizar Pago
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditarPago;
